import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { setLastRead } from '../../../api/user_conversations';
import { sendMessage } from '../../../api/messages';
import { getBlockedSlots } from '../../../api/v2/calls';
import { createActivity } from '../../../api/activities';
import { pageInspect } from '../../../api/metainspector';
import { isObjEmpty } from '../../../helpers';
import { isFeatureEnabled, isPhoneCallsEnabled, FEATURES } from '../../../helpers/feature_checker.js';
import { find as linkifyFind } from 'linkifyjs';

import ComplianceAcknowledgmentModal from './compliance_acknowledgment_modal/index_gated.jsx';
import PostConversationSurveyBanner from '../../questionnaire/post_conversation_survey_banner';
import MessageTriggerNote from './message_trigger_note';
import TranslationToggle, { getLanguageName, isSupportedTranslationLanguage } from './translation_toggle';
import PhoneCall from '../../phone_calls/phone_call';
import MentorCallStatus from '../../phone_calls/mentor_call_status';
import ActiveCallStatus from '../../phone_calls/active_call_status';
import PatientIncomingCallStatus from '../../phone_calls/patient_incoming_call_status';
import AvailabilityPicker from '../../phone_calls/availability_picker';
import PlatformModal from '../../ui_kit/modals/platform_modal';
import Context from '../../../utils/context';
import TextareaAutosize from 'react-textarea-autosize';
import AvatarWActive from '../../ui_kit/utilities/avatar-w-active';
import ActionIcons from '../../system/action_icons';
import { useDebouncedCallback } from 'use-debounce';
import UrlPreview from '../../ui_kit/utilities/url_preview';
import LoadingScreen from '../../system/loading_screen';
import DOMPurify from '../../../utils/purify.min.js';
import S3UploadFile from '../../ui_kit/utilities/s3_upload_file';
import ImageGridDisplay from '../../ui_kit/utilities/image_grid_display';
import linkifyHtml from 'linkify-html';

import styles from './legacy_conversation_room.module.css';

export default function Conversation({
  conversation,
  user,
  backPath,
  profilePath,
  fetchConversation,
  otherUser,
  numConversationsWithUnreadMessages,
  sendFunction = () => {},
  showSurveyBanner = false,
  surveyMentorName,
  onStartSurvey,
}) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [currentMessage, setCurrentMessage] = useState(searchParams.get('firstMessage') || '');
  const lastReadTime = conversation.current_user_conversation.last_read_time;
  const [heldKey, setHeldKey] = useState(null);
  const context = useContext(Context);
  const viewport = context.viewport;
  const currentAccount = context.auth.currentAccount();
  const isPhoneFeatureEnabled = isPhoneCallsEnabled(currentAccount);
  const isPhoneCallsOnly = isFeatureEnabled(currentAccount, FEATURES.PHONE_CALLS_ONLY);
  const isTranslationFeatureEnabled = isFeatureEnabled(currentAccount, FEATURES.TRANSLATIONS);
  const isTriggerNotesEnabled = isFeatureEnabled(currentAccount, FEATURES.MESSAGE_TRIGGER_NOTES);
  const isAttachmentsDisabled = isFeatureEnabled(currentAccount, FEATURES.DISALLOW_ATTACHMENTS_IN_MESSAGES);
  const isAcceptingCalls = otherUser && otherUser?.profile?.accepts_calls;
  const showPhoneFeature = isPhoneFeatureEnabled && isAcceptingCalls;
  const showMessageInput = Boolean(!isPhoneCallsOnly);
  const [complianceAcknowledgedAt, setComplianceAcknowledgedAt] = useState(
    conversation.current_user_compliance_acknowledged_at || null
  );

  const showComplianceModal = conversation.compliance_acknowledgment_required && !complianceAcknowledgedAt;
  const isMobile = viewport.getIsMobile();
  const isMentor = user?.current_profile_type === 'PatientPartner';
  const [activeCall, setActiveCall] = useState(conversation.outgoing_call || null);
  const [incomingCall, setIncomingCall] = useState(conversation.incoming_call || null);
  const [mentorActiveCall, setMentorActiveCall] = useState(conversation.conversation_call || null);
  const [counteringCall, setCounteringCall] = useState(false);
  const [counteringCallId, setCounteringCallId] = useState(null);
  const [patientCounteringCall, setPatientCounteringCall] = useState(false);
  const [incomingCallBlockedSlots, setIncomingCallBlockedSlots] = useState([]);
  const [urlPreview, setUrlPreview] = useState();
  const [isUrlPreviewLoading, setIsUrlPreviewLoading] = useState(false);
  const [urlMetadata, setUrlMetadata] = useState(null);
  const messageInputRef = useRef(null);
  const [messageInputHeight, setMessageInputHeight] = useState(0);
  const currentTranslationLanguage = conversation.current_user_conversation?.translate_to_language;
  const [targetLanguage, setTargetLanguage] = useState(
    isSupportedTranslationLanguage(currentTranslationLanguage) ? currentTranslationLanguage : null
  );

  const otherUserImageUrl = otherUser?.profile?.photo || '';

  useEffect(() => {
    setComplianceAcknowledgedAt(conversation.current_user_compliance_acknowledged_at || null);
  }, [conversation.uuid, conversation.current_user_compliance_acknowledged_at]);

  useEffect(() => {
    const nextLanguage = conversation.current_user_conversation?.translate_to_language;
    setTargetLanguage(isSupportedTranslationLanguage(nextLanguage) ? nextLanguage : null);
  }, [conversation.uuid, conversation.current_user_conversation?.translate_to_language]);

  useEffect(() => {
    if (!isMobile) {
      document.body.style.overflow = '';
      return;
    }

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile]);

  // Track the height of the message input container
  useEffect(() => {
    if (messageInputRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          setMessageInputHeight(entry.contentRect.height);
        }
      });

      resizeObserver.observe(messageInputRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  useEffect(() => {
    setActiveCall(conversation.outgoing_call || null);
  }, [conversation.outgoing_call?.uuid, conversation.outgoing_call?.status, conversation.outgoing_call?.counter_count]);

  useEffect(() => {
    setIncomingCall(conversation.incoming_call || null);
  }, [conversation.incoming_call?.uuid, conversation.incoming_call?.status, conversation.incoming_call?.counter_count]);

  useEffect(() => {
    setMentorActiveCall(conversation.conversation_call || null);
  }, [
    conversation.conversation_call?.uuid,
    conversation.conversation_call?.status,
    conversation.conversation_call?.countered_by_type,
  ]);

  useEffect(() => {
    if (!isMentor && incomingCall?.scheduled) {
      getBlockedSlots(conversation.uuid)
        .then((data) => setIncomingCallBlockedSlots(data.blocked_slots || []))
        .catch(() => setIncomingCallBlockedSlots([]));
    }
  }, [isMentor, incomingCall?.uuid, incomingCall?.scheduled, conversation.uuid]);

  let defaultFiles = [];
  if (currentMessage.message_images) {
    defaultFiles = currentMessage.message_images.map((i) => i.image);
  }
  const [files, setFiles] = useState(defaultFiles);
  const send = (e) => {
    if (e !== undefined) {
      e.preventDefault();
    }

    if (currentMessage.replace(/\n/g, '') === '' && files.length === 0) {
      return;
    }
    setCurrentMessage('');
    onSendClick(currentMessage, urlMetadata);
    setFiles([]);
  };
  let suggestedMessages = [];
  if (user.current_profile_type === 'Patient' && conversation && conversation.messages.length === 0) {
    suggestedMessages = [
      'How did you find a surgeon?',
      'What was recovery like?',
      'How do I prepare for my upcoming surgery?',
      'How did surgery change your life?',
      'How can my loved ones support me during surgery?',
      'Where can i find support if I do not have it during surgery?',
    ];
  }

  const onRead = () => {
    setLastRead(conversation.id, () => fetchConversation());
  };

  const onSendClick = (currentMessage, urlMetadata) => {
    createActivity('Activity::InitiateConnection', { conversation_id: conversation.uuid });
    sendMessage(conversation.uuid, currentMessage, urlMetadata, files, () => {
      setUrlPreview(null);
      setUrlMetadata(null);
      fetchConversation(false, true);
    });
    sendFunction();
  };

  const clickNewMessages = () => {
    document.querySelector('.message-container-otr').scrollTop = 0;
  };

  const showUrlPreview = (newBody) => {
    const link = linkifyFind(newBody).find((l) => l.type === 'url');
    if (link && link.href !== urlPreview) {
      setUrlPreview(link.href);
      pageInspect(link.href, (data) => {
        if (!isObjEmpty(data) && data.title && data.description && data.image) {
          setUrlMetadata(data);
        }
        setIsUrlPreviewLoading(false);
      });
      setIsUrlPreviewLoading(true);
    }
  };

  const removeUrlPreview = () => {
    setUrlMetadata(null);
    setUrlPreview(null);
  };

  const debouncedShowUrlPreview = useDebouncedCallback((value) => {
    showUrlPreview(value);
  }, 1000);

  const onBodyChange = (event) => {
    setCurrentMessage(event.target.value);
    debouncedShowUrlPreview(event.target.value);
  };

  return (
    <div className={styles.chat_room} style={{ flex: 1, minHeight: 0 }}>
      {showComplianceModal && (
        <ComplianceAcknowledgmentModal
          conversationUuid={conversation.uuid}
          accountSlug={currentAccount?.slug}
          role={isMentor ? 'mentor' : 'patient'}
          onAcknowledged={(acknowledgedAt) => setComplianceAcknowledgedAt(acknowledgedAt)}
        />
      )}
      <div
        className={`container ${styles.chat_room_header}`}
        style={isMobile ? { borderBottom: '1px solid rgba(196, 196, 196, 0.44)', paddingBottom: 'none' } : null}
      >
        <div className='row'>
          {otherUser && (
            <>
              {isMobile && backPath && (
                <div style={{ cursor: 'pointer' }} className='col-1 my-auto'>
                  <Link to={backPath} style={{ marginTop: '6px', display: 'block' }}>
                    <svg width='13' height='23' viewBox='0 0 13 23' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M12.183 2.56066C12.7688 1.97487 12.7688 1.02513 12.183 0.43934C11.5972 -0.146447 10.6475 -0.146447 10.0617 0.43934L0.43934 10.0617C-0.146447 10.6475 -0.146447 11.5972 0.43934 12.183L10.0617 21.8054C10.6475 22.3912 11.5972 22.3912 12.183 21.8054C12.7688 21.2196 12.7688 20.2699 12.183 19.6841L3.62132 11.1224L12.183 2.56066Z'
                        fill='#59B6BE'
                      />
                    </svg>
                    {numConversationsWithUnreadMessages > 0 && numConversationsWithUnreadMessages < 99 && (
                      <div className={`notif-number-bub ${styles.convo_arrow_notif}`}>
                        {numConversationsWithUnreadMessages}
                      </div>
                    )}
                    {numConversationsWithUnreadMessages > 99 && <div className='notif-number-bub'>99+</div>}
                  </Link>
                </div>
              )}
              {conversation && (
                <div className={`col ${styles.chat_room_pp_info}`}>
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <div>
                      {isMobile && profilePath ? (
                        <Link to={profilePath}>
                          <AvatarWActive profile={otherUser.profile} showInfo={true} />
                        </Link>
                      ) : (
                        <AvatarWActive profile={otherUser.profile} showInfo={true} />
                      )}
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {isTranslationFeatureEnabled && (
                        <TranslationToggle
                          conversationUuid={conversation.uuid}
                          initialLanguage={conversation.current_user_conversation?.translate_to_language}
                          onLanguageChange={(lang) => {
                            setTargetLanguage(lang);
                            fetchConversation(false);
                          }}
                        />
                      )}
                      {showPhoneFeature && (
                        <PhoneCall
                          otherUser={otherUser}
                          conversationId={conversation.uuid}
                          activeCall={activeCall}
                          onCallCreated={setActiveCall}
                          currentUserTimezone={user?.timezone}
                        />
                      )}
                      <ActionIcons style={{ alignSelf: 'flex-end' }} />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {!isMentor && incomingCall && incomingCall.scheduled && (
        <PatientIncomingCallStatus
          incomingCall={incomingCall}
          patientTimezone={user?.timezone}
          mentorFirstName={otherUser?.profile?.display_name?.split(' ')[0] || 'Mentor'}
          blockedSlots={incomingCallBlockedSlots}
          onAccepted={(updatedCall) => {
            setIncomingCall(null);
            setActiveCall(updatedCall);
          }}
          onDeclined={() => setIncomingCall(null)}
        />
      )}

      {!isMentor && activeCall && !(incomingCall && incomingCall.scheduled) && (
        <ActiveCallStatus
          activeCall={activeCall}
          patientTimezone={user?.timezone}
          patientPhone={user?.phone}
          mentorFirstName={otherUser?.profile?.display_name?.split(' ')[0] || 'your mentor'}
          onCancel={() => setActiveCall(null)}
          onExpire={() => setActiveCall(null)}
          onAccepted={(updatedCall) => setActiveCall(updatedCall)}
          onCounter={() => setPatientCounteringCall(true)}
        />
      )}

      {isMentor && (incomingCall || mentorActiveCall) && !counteringCall && (
        <MentorCallStatus
          incomingCall={incomingCall}
          mentorTimezone={user?.timezone}
          mentorPhone={user?.phone}
          acceptedCall={
            mentorActiveCall &&
            (mentorActiveCall.status === 'accepted' || mentorActiveCall.status === 'in_progress') &&
            !incomingCall
              ? mentorActiveCall
              : null
          }
          patientFirstName={otherUser?.profile?.display_name?.split(' ')[0] || 'Patient'}
          onAccepted={(updatedCall) => {
            setIncomingCall(null);
            setMentorActiveCall(updatedCall);
          }}
          onDeclined={() => setIncomingCall(null)}
          onCounter={() => {
            setCounteringCallId(incomingCall?.uuid);
            setCounteringCall(true);
          }}
          onCancel={() => setMentorActiveCall(null)}
        />
      )}

      {counteringCall && (
        <AvailabilityPicker
          mentorTimezone={user?.timezone}
          viewerTimezone={user?.timezone}
          mentorFirstName={otherUser?.profile?.display_name?.split(' ')[0] || 'Patient'}
          otherUserName={otherUser?.profile?.display_name}
          otherUserPhoto={otherUser?.profile?.photo}
          conversationId={conversation.uuid}
          existingCallId={counteringCallId}
          onClose={() => setCounteringCall(false)}
          onCallCreated={() => setCounteringCall(false)}
        />
      )}

      {patientCounteringCall && (
        <AvailabilityPicker
          mentorTimezone={otherUser?.user?.timezone}
          viewerTimezone={user?.timezone}
          mentorFirstName={otherUser?.profile?.display_name?.split(' ')[0] || 'your mentor'}
          otherUserName={otherUser?.profile?.display_name}
          otherUserPhoto={otherUser?.profile?.photo}
          conversationId={conversation.uuid}
          existingCallId={activeCall?.uuid}
          onClose={() => setPatientCounteringCall(false)}
          onCallCreated={() => setPatientCounteringCall(false)}
        />
      )}

      {conversation && conversation.messages.length === 0 ? (
        <div
          style={{
            paddingTop: '48px',
            paddingBottom: '24px',
            width: '100%',
            textAlign: 'center',
            flexFlow: 'column',
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div
            className={`${
              otherUser.profile_type === 'SuperUser'
                ? styles.chat_room_super_user_avatar_lg
                : styles.chat_room_pp_avatar_lg
            } marg-bottom-16 marg-top-0`}
            style={{ backgroundImage: `url("${otherUserImageUrl}")`, height: '72px', width: '72px' }}
          />
          <p className='bold' style={{ fontSize: '16px', marginBottom: '4px' }}>
            {otherUser.profile.display_name}
          </p>
          <p style={{ color: '#9e9e9e', fontSize: '14px', marginBottom: '0' }}>Say hello to start the conversation</p>
        </div>
      ) : (
        <div className='message-container-otr'>
          <div className='message-container'>
            <div className='container'>
              <div className='row'>
                <div className='col'>
                  {conversation &&
                    lastReadTime !== null &&
                    conversation.messages.map((message, index) => {
                      const currentMessageCurrentUser = message.current_user_sent;
                      const previousMessageCurrentUser =
                        index - 1 !== -1 && conversation.messages[index - 1].current_user_sent;
                      return (
                        <React.Fragment key={message.id || index}>
                          {((index - 1 === -1) === true ||
                            (new Date(message.created_at).getDate() !==
                              new Date(conversation.messages[index - 1].created_at).getDate() &&
                              index - 1 !== -1)) && (
                            <p className={`text-center ${styles.message_date}`}>{`${new Date(
                              message.created_at
                            ).toLocaleString('default', { month: 'short' })} ${new Date(
                              message.created_at
                            ).getDate()}`}</p>
                          )}
                          {index === 0 &&
                            !message.current_user_sent &&
                            new Date(message.created_at).getTime() > new Date(lastReadTime).getTime() && (
                              <div
                                onClick={() => {
                                  clickNewMessages();
                                  onRead();
                                }}
                                className={styles.new_message_txt_wrap}
                              >
                                <div className={styles.new_message_txt}>
                                  <h6>New Messages</h6>
                                  <svg
                                    width='15'
                                    height='8'
                                    viewBox='0 0 15 8'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                  >
                                    <path
                                      d='M1.00177 1.84135e-05C1.23542 -0.000438079 1.46185 0.0809368 1.64177 0.230018L7.00177 4.71002L12.3718 0.39002C12.4741 0.306954 12.5918 0.244922 12.7181 0.20749C12.8444 0.170059 12.9769 0.157965 13.1079 0.171904C13.239 0.185843 13.366 0.22554 13.4816 0.288714C13.5972 0.351888 13.6992 0.437293 13.7818 0.540019C13.8648 0.642307 13.9269 0.760003 13.9643 0.886343C14.0017 1.01268 14.0138 1.14517 13.9999 1.2762C13.9859 1.40723 13.9462 1.53421 13.8831 1.64985C13.8199 1.76549 13.7345 1.8675 13.6318 1.95002L7.63177 6.78002C7.45283 6.9271 7.22839 7.00751 6.99677 7.00751C6.76514 7.00751 6.5407 6.9271 6.36177 6.78002L0.361767 1.78002C0.260509 1.69607 0.176806 1.59297 0.115454 1.47662C0.0541022 1.36027 0.016305 1.23297 0.00422911 1.10199C-0.00784675 0.971013 0.00603624 0.838941 0.0450807 0.713337C0.0841252 0.587734 0.147564 0.471067 0.231766 0.370018C0.32482 0.255305 0.442132 0.162631 0.575268 0.098656C0.708405 0.0346812 0.85406 0.000995185 1.00177 1.84135e-05Z'
                                      fill='white'
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          {index - 1 !== -1 &&
                            !message.current_user_sent &&
                            new Date(message.created_at).getTime() > new Date(lastReadTime).getTime() &&
                            new Date(conversation.messages[index - 1].created_at).getTime() <
                              new Date(lastReadTime).getTime() && (
                              <div
                                onClick={() => {
                                  clickNewMessages();
                                  onRead();
                                }}
                                className={styles.new_message_txt_wrap}
                              >
                                <div className={styles.new_message_txt}>
                                  <h6>New Messages</h6>
                                  <svg
                                    width='15'
                                    height='8'
                                    viewBox='0 0 15 8'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                  >
                                    <path
                                      d='M1.00177 1.84135e-05C1.23542 -0.000438079 1.46185 0.0809368 1.64177 0.230018L7.00177 4.71002L12.3718 0.39002C12.4741 0.306954 12.5918 0.244922 12.7181 0.20749C12.8444 0.170059 12.9769 0.157965 13.1079 0.171904C13.239 0.185843 13.366 0.22554 13.4816 0.288714C13.5972 0.351888 13.6992 0.437293 13.7818 0.540019C13.8648 0.642307 13.9269 0.760003 13.9643 0.886343C14.0017 1.01268 14.0138 1.14517 13.9999 1.2762C13.9859 1.40723 13.9462 1.53421 13.8831 1.64985C13.8199 1.76549 13.7345 1.8675 13.6318 1.95002L7.63177 6.78002C7.45283 6.9271 7.22839 7.00751 6.99677 7.00751C6.76514 7.00751 6.5407 6.9271 6.36177 6.78002L0.361767 1.78002C0.260509 1.69607 0.176806 1.59297 0.115454 1.47662C0.0541022 1.36027 0.016305 1.23297 0.00422911 1.10199C-0.00784675 0.971013 0.00603624 0.838941 0.0450807 0.713337C0.0841252 0.587734 0.147564 0.471067 0.231766 0.370018C0.32482 0.255305 0.442132 0.162631 0.575268 0.098656C0.708405 0.0346812 0.85406 0.000995185 1.00177 1.84135e-05Z'
                                      fill='white'
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          {index - 1 === -1 && (
                            // show timestamp for the very first message
                            <p
                              style={{ textAlign: message.current_user_sent ? 'right' : 'left' }}
                              className={`${styles.message_date} marg-top-10`}
                            >{`${new Date(message.created_at).toLocaleTimeString()}`}</p>
                          )}
                          {index - 1 !== -1 &&
                            (message.current_user_sent !== conversation.messages[index - 1].current_user_sent) ===
                              true &&
                            new Date(message.created_at).getMinutes() ===
                              new Date(conversation.messages[index - 1].created_at).getMinutes() && (
                              // show timestamp if the message is from a different user than the one above
                              <p
                                style={{ textAlign: message.current_user_sent ? 'right' : 'left' }}
                                className={`${styles.message_date} marg-top-10`}
                              >{`${new Date(message.created_at).toLocaleTimeString()}`}</p>
                            )}
                          {index - 1 !== -1 &&
                            new Date(message.created_at).getMinutes() !==
                              new Date(conversation.messages[index - 1].created_at).getMinutes() && (
                              // show timestamp if the message above is a different minute
                              <p
                                style={{ textAlign: message.current_user_sent ? 'right' : 'left' }}
                                className={`${styles.message_date} marg-top-10`}
                              >{`${new Date(message.created_at).toLocaleTimeString()}`}</p>
                            )}
                          {currentMessageCurrentUser && (
                            <>
                              <div
                                style={
                                  message.message_images.length > 0 ? { width: 'auto' } : { width: ' max-content' }
                                }
                                className={`${styles.message_bubble} ${styles.current_user} ${
                                  index - 1 !== -1 ? (!previousMessageCurrentUser ? 'marg-top-20' : '') : ''
                                }`}
                              >
                                <p
                                  dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                      linkifyHtml(message.body.replaceAll('\n', '<br/>'), { target: 'blank' })
                                    ),
                                  }}
                                ></p>
                                {message.url_preview_metadata && <UrlPreview metadata={message.url_preview_metadata} />}
                                {message.message_images.length > 0 && (
                                  <div className='marg-top-10 marg-bottom-10'>
                                    <ImageGridDisplay photos={message.message_images.map((i) => i.image)} />
                                  </div>
                                )}
                              </div>
                              {isTriggerNotesEnabled &&
                                message.triggered_notes?.map((note, noteIndex) => (
                                  <MessageTriggerNote key={noteIndex} note={note} />
                                ))}
                            </>
                          )}
                          {!currentMessageCurrentUser && (
                            <>
                              <div
                                style={
                                  message.message_images.length > 0 ? { width: 'auto' } : { width: ' max-content' }
                                }
                                className={`${styles.message_bubble} ${styles.other_user} fs-mask ${
                                  index - 1 !== -1 ? (previousMessageCurrentUser ? 'marg-top-20' : '') : ''
                                }`}
                              >
                                <p
                                  dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                      linkifyHtml(message.body.replaceAll('\n', '<br/>'), { target: 'blank' })
                                    ),
                                  }}
                                ></p>
                                {message.url_preview_metadata && <UrlPreview metadata={message.url_preview_metadata} />}
                                {message.message_images.length > 0 && (
                                  <div className='marg-top-10 marg-bottom-10'>
                                    <ImageGridDisplay photos={message.message_images.map((i) => i.image)} />
                                  </div>
                                )}
                              </div>
                              {isTriggerNotesEnabled &&
                                message.triggered_notes?.map((note, noteIndex) => (
                                  <MessageTriggerNote key={noteIndex} note={note} />
                                ))}
                              {isTranslationFeatureEnabled &&
                                targetLanguage &&
                                message.translations?.[targetLanguage] && (
                                  <div className={`${styles.translated_box} fs-mask`}>
                                    <div className={styles.translated_box_label}>
                                      {getLanguageName(targetLanguage)} Translation
                                    </div>
                                    {message.translations[targetLanguage]}
                                  </div>
                                )}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {conversation && (
        <div>
          <div ref={messageInputRef} className={`container ${styles.message_otr}`}>
            {showSurveyBanner && (
              <PostConversationSurveyBanner
                mentorName={surveyMentorName || otherUser?.profile?.display_name?.split(' ')[0]}
                onStart={onStartSurvey}
              />
            )}
            <div className='row' style={{ height: files.length === 0 ? '0px' : '' }}>
              <div className='col-12 marg-top-10'>
                <ImageGridDisplay photos={files} setPhotos={setFiles} type={'inline'} allowRemove />
              </div>
            </div>
            {showMessageInput && (
              <div className={styles.type_message_row}>
                {!isAttachmentsDisabled && (
                  <div className={styles.upload_btn_col}>
                    <S3UploadFile
                      onChange={(newFiles) => {
                        setFiles([...files, ...newFiles]);
                      }}
                      type='icon'
                      name='filename'
                      multiple={true}
                      cta=''
                    />
                  </div>
                )}
                <div className={styles.message_input_col}>
                  <TextareaAutosize
                    className={`material-input ${styles.message_box}`}
                    value={currentMessage}
                    onChange={(e) => onBodyChange(e)}
                    onKeyDown={(e) => {
                      e.key === 'Enter' && heldKey !== 'Shift' ? send(e) : e.key === 'Shift' ? setHeldKey(e.key) : null;
                    }}
                    onKeyUp={(e) => {
                      e.key === heldKey ? setHeldKey(null) : null;
                    }}
                    onFocus={() => onRead()}
                    placeholder={
                      suggestedMessages.length !== 0
                        ? 'Introduce yourself...'
                        : `Message ${otherUser.profile.display_name}`
                    }
                    maxRows={15}
                    style={{ minHeight: '64px' }}
                  />
                  <div style={{ marginTop: '10px' }}>
                    {isUrlPreviewLoading && <LoadingScreen size={'20'} />}
                    {!isUrlPreviewLoading && urlMetadata && (
                      <UrlPreview metadata={urlMetadata} onDelete={removeUrlPreview} />
                    )}
                  </div>
                </div>
                <div className={styles.send_message_col}>
                  <button
                    className={`${styles.send_message_button} ${
                      currentMessage === '' && files.length === 0 ? styles.disabled : ''
                    }`}
                    onClick={send}
                  >
                    <svg width='16' height='18' viewBox='0 0 16 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M15.5162 8.09473L1.20138 0.095958C1.05725 0.0149865 0.896708 -0.0156778 0.738463 0.00753903C0.580218 0.0307558 0.43079 0.106898 0.307592 0.22709C0.184395 0.347282 0.0925034 0.506575 0.0426271 0.686402C-0.00724916 0.86623 -0.0130558 1.05919 0.025884 1.24278L1.65861 8.99959L0.025884 16.7564C-0.0134202 16.9401 -0.00786745 17.1332 0.0418927 17.3133C0.0916529 17.4933 0.183563 17.6528 0.306872 17.7731C0.430181 17.8934 0.57979 17.9696 0.738199 17.9926C0.896607 18.0156 1.05727 17.9846 1.20138 17.9032L15.5162 9.90446C15.6608 9.82371 15.7831 9.69574 15.8688 9.53551C15.9545 9.37528 16 9.1894 16 8.99959C16 8.80979 15.9545 8.62391 15.8688 8.46368C15.7831 8.30344 15.6608 8.17548 15.5162 8.09473V8.09473ZM2.08974 15.1956L2.79622 11.8392L7.57904 8.99959L2.79622 6.16003L2.08974 2.80354L13.1795 8.99959L2.08974 15.1956Z'
                        fill='#74CCD3'
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
